/**
 * Quotify injector — cross-origin via postMessage
 *
 * Quotify's form lives inside a cross-origin iframe (quotify.dev), so we
 * can't touch its DOM directly. We send messages to the iframe using
 * window.postMessage. Listeners inside Quotify's Custom JavaScript then
 * populate the matching hidden fields.
 *
 * Payloads currently sent:
 *   - MS_GCLID:        gclid + msclkid for marketing attribution
 *   - MS_ATC_PRICE:    variant's custom.add_to_cart_price metafield value
 *                      (single, for the currently-selected variant)
 *   - MS_ATC_PRICES:   full per-variant ATC price lookup table from
 *                      window.MS_ATC_PRICES (emitted by main-product.liquid's
 *                      price_custom block)
 *   - MS_ACCESSORIES:  selected accessory line items + discount %
 *   - MS_FREE_GIFTS:   free-gift promo lines
 *   - MS_CONFIGURATION: required configuration options (e.g. sample block)
 *                      from custom.required_configuration + the product-page
 *                      pick, so the quote form can render the same picker
 *   - MS_CART_PRICING: CART PAGE ONLY — human-readable snapshot of the live
 *                      Shopify cart with per-line original→final pricing and
 *                      cart-level discount totals. Used so the "Convert cart
 *                      to quote" flow surfaces bundle-app discounts that
 *                      Quotify otherwise discards when it re-prices from
 *                      catalog. Strictly gated so it does NOT fire on
 *                      product-page quotes — only when the customer opens
 *                      Quotify from the /cart page.
 *   - MS_LIVECHAT:     whether the customer engaged with LiveChat in the
 *                      last 30 days ("Yes – chat <id> (<date>)" or "No").
 *                      Set by livechat-quote-tracker.js (ms_livechat cookie).
 */
(function () {
  function findQuotifyFrame() {
    return document.getElementById('quotify-frame');
  }

  function getCurrentVariantId() {
    try {
      var url = new URL(window.location.href);
      var v = url.searchParams.get('variant');
      if (v) return String(v);
    } catch (_) {}
    return null;
  }

  function getCurrentAtcPrice() {
    var prices = window.MS_ATC_PRICES;
    if (!prices) return null;
    var id = getCurrentVariantId();
    if (id && prices[id]) return prices[id];
    // Fallback: first variant in the map
    var keys = Object.keys(prices);
    return keys.length ? prices[keys[0]] : null;
  }

  function sendGclid() {
    var gclid = window.MS_GCLID || '';
    var msclkid = window.MS_MSCLKID || '';
    if (!gclid && !msclkid) return;
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage(
        { type: 'MS_GCLID', gclid: gclid, msclkid: msclkid },
        '*' // target '*' is safe — these are non-sensitive tracking IDs
      );
    } catch (_) {}
  }

  function sendAtcPrice() {
    var price = getCurrentAtcPrice();
    if (!price) return;
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage(
        {
          type: 'MS_ATC_PRICE',
          amount: price.amount, // e.g. "1999.00"
          currency: price.currency, // e.g. "USD"
          formatted: price.formatted, // e.g. "$1,999.00"
          variantId: getCurrentVariantId(),
        },
        '*'
      );
    } catch (_) {}
  }

  // Send the full per-variant ATC price lookup so the Quotify side can
  // override the price on every variant row in the Step 1 table (not just
  // the currently-selected one).
  function sendAtcPricesAll() {
    var prices = window.MS_ATC_PRICES;
    if (!prices || typeof prices !== 'object') return;
    var keys = Object.keys(prices);
    if (keys.length === 0) return;
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage(
        { type: 'MS_ATC_PRICES', prices: prices },
        '*'
      );
    } catch (_) {}
  }

  function sendAccessories() {
    var accessories = window.MS_ACCESSORIES;
    if (!Array.isArray(accessories) || accessories.length === 0) return;
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage(
        {
          type: 'MS_ACCESSORIES',
          accessories: accessories,
          discountPercent: window.MS_ACCESSORIES_DISCOUNT_PERCENT || 10,
          // Max top-level accessories the customer may pick (0 = unlimited).
          // From custom.accessories_max_select via product-accessories-block.liquid.
          maxSelect: window.MS_ACCESSORIES_MAX_SELECT || 0,
        },
        '*'
      );
    } catch (_) {}
  }

  function sendConfiguration() {
    var cfg = window.MS_CONFIGURATION;
    if (!cfg || !Array.isArray(cfg.options) || cfg.options.length === 0) return;
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage({ type: 'MS_CONFIGURATION', configuration: cfg }, '*');
    } catch (_) {}
  }

  function sendFreeGifts() {
    var promos = window.MS_FREE_GIFTS;
    if (!Array.isArray(promos) || promos.length === 0) return;
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage(
        { type: 'MS_FREE_GIFTS', promos: promos },
        '*'
      );
    } catch (_) {}
  }

  // Extended warranty add-on. Amount is recomputed per selected variant by
  // accessories-block.js (it rewrites window.MS_WARRANTY.amount*), so we just
  // forward the latest snapshot into the Quotify iframe on every send.
  function sendWarranty() {
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    var w = window.MS_WARRANTY;
    if (w && w.enabled) {
      try {
        frame.contentWindow.postMessage({ type: 'MS_WARRANTY', warranty: w }, '*');
      } catch (_) {}
    }
    // Full list of warranty picks across products (from localStorage) so a
    // multi-product quote can show a warranty line per product.
    var list = [];
    try {
      var map = JSON.parse(localStorage.getItem('MS_WARRANTY_PICKS') || '{}') || {};
      for (var k in map) { if (map[k]) list.push(map[k]); }
    } catch (_) {}
    try {
      frame.contentWindow.postMessage({ type: 'MS_WARRANTY_LIST', warranties: list }, '*');
    } catch (_) {}
  }

  // The in-quote warranty checkbox (inside the Quotify iframe) posts its state
  // up here, so a pick made inside the quote persists into the same localStorage
  // map the product-page tile uses — keeping a multi-product quote complete no
  // matter where the customer ticked the box.
  window.addEventListener('message', function (e) {
    var d = e && e.data;
    if (!d || d.type !== 'MS_WARRANTY_SET' || !d.handle) return;
    try {
      var map = JSON.parse(localStorage.getItem('MS_WARRANTY_PICKS') || '{}') || {};
      if (d.selected) {
        map[d.handle] = { handle: d.handle, title: d.title || '', amount: d.amount || '' };
      } else {
        delete map[d.handle];
      }
      localStorage.setItem('MS_WARRANTY_PICKS', JSON.stringify(map));
    } catch (_) {}
  });

  // ---------------------------------------------------------------------
  // Cart-pricing snapshot (CART PAGE ONLY)
  //
  // When "Convert cart to quote" is used, Quotify only carries product,
  // variant, qty, and string line-item properties — it then re-prices each
  // line from the catalog. That throws away anything a Shopify Bundles app
  // (or any cart-level discount) has applied.
  //
  // To preserve that info, we read /cart.js, build a readable summary that
  // includes per-line original → final pricing and the cart-level discount
  // total, and stuff it into a hidden Quotify form field via postMessage.
  // The merchant sees the cart's actual pricing on every quote and can
  // match it when generating the proposal.
  //
  // This is STRICTLY GATED to the cart page via isCartPage() so it never
  // fires on product-page quotes (where the customer is asking about a
  // single product, not their cart contents).
  // ---------------------------------------------------------------------

  // True only when the current URL path is the cart page. Tolerant of
  // Shopify market prefixes (e.g. /en/cart, /fr-FR/cart) and trailing
  // slashes; false for /cart-anything-else.
  function isCartPage() {
    var p = (window.location.pathname || '').toLowerCase();
    return /(^|\/)cart(\/|$)/.test(p);
  }

  var cartPricingCache = null; // string, refreshed on cart change

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var sign = cents < 0 ? '-' : '';
    var abs = Math.abs(cents);
    var dollars = (abs / 100).toFixed(2);
    // Thousands separators
    var parts = dollars.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return sign + '$' + parts.join('.');
  }

  function buildCartPricingSnapshot(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return null;
    }
    var lines = [];
    cart.items.forEach(function (item) {
      var title = item.product_title || item.title || 'Item';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        title += ' — ' + item.variant_title;
      }
      var sku = item.sku ? ' [' + item.sku + ']' : '';
      var qty = item.quantity || 1;
      var original = item.original_line_price; // pre-discount, cents
      var finalp = item.final_line_price; // post-discount, cents
      var perUnit =
        typeof finalp === 'number' && qty
          ? ' (' + formatMoney(Math.round(finalp / qty)) + ' ea)'
          : '';
      var row = '- ' + title + sku + ' × ' + qty;
      if (typeof original === 'number' && typeof finalp === 'number') {
        if (original === finalp) {
          row += ': ' + formatMoney(finalp) + perUnit;
        } else {
          row +=
            ': ' +
            formatMoney(original) +
            ' → ' +
            formatMoney(finalp) +
            perUnit;
        }
      }
      // Surface any bundle/discount allocation names attached to this line
      if (
        Array.isArray(item.line_level_discount_allocations) &&
        item.line_level_discount_allocations.length
      ) {
        item.line_level_discount_allocations.forEach(function (alloc) {
          var label =
            (alloc.discount_application && alloc.discount_application.title) ||
            'Discount';
          row +=
            '\n    • ' + label + ': -' + formatMoney(alloc.amount || 0);
        });
      }
      lines.push(row);
    });

    var header = [
      'Cart subtotal: ' + formatMoney(cart.items_subtotal_price),
      'Cart-level discount: -' + formatMoney(cart.total_discount || 0),
      'Effective total (before shipping/tax): ' +
        formatMoney(cart.total_price),
    ];

    return (
      header.join('\n') +
      '\n\nLine items (cart-applied pricing):\n' +
      lines.join('\n')
    );
  }

  function refreshCartPricing() {
    // Hard gate: never fetch / cache on non-cart pages.
    if (!isCartPage()) {
      cartPricingCache = null;
      return Promise.resolve(null);
    }
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        cartPricingCache = buildCartPricingSnapshot(cart);
        return cartPricingCache;
      })
      .catch(function () {
        return null;
      });
  }

  function sendCartPricing() {
    // Hard gate: never post the snapshot from non-cart pages, even if a
    // stale cache survived a SPA-style navigation.
    if (!isCartPage()) return;
    if (!cartPricingCache) return;
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage(
        { type: 'MS_CART_PRICING', snapshot: cartPricingCache },
        '*'
      );
    } catch (_) {}
  }

  // LiveChat attribution (see livechat-quote-tracker.js).
  function liveChatValue() {
    var raw = window.MS_LIVECHAT || '';
    if (!raw) {
      var m = document.cookie.match(/(?:^|; )ms_livechat=([^;]*)/);
      raw = m ? decodeURIComponent(m[1]) : '';
    }
    if (!raw) return 'No';
    var parts = String(raw).split('|');
    return 'Yes – chat ' + (parts[1] || 'unknown') + ' (' + (parts[0] || '?') + ')';
  }

  function sendLiveChat() {
    var frame = findQuotifyFrame();
    if (!frame || !frame.contentWindow) return;
    try {
      frame.contentWindow.postMessage({ type: 'MS_LIVECHAT', value: liveChatValue() }, '*');
    } catch (_) {}
  }

  function sendAll() {
    sendGclid();
    sendAtcPrice();
    sendAtcPricesAll();
    sendAccessories();
    sendFreeGifts();
    sendConfiguration();
    sendWarranty();
    sendCartPricing(); // self-gated to cart page
    sendLiveChat();
  }

  function start() {
    // Cart-pricing snapshot setup — only wire any of this if we're on the
    // cart page. Saves a /cart.js fetch on every product page load.
    if (isCartPage()) {
      // Initial fetch
      refreshCartPricing();

      // Shopify themes fire various cart-update events depending on theme/app
      // wiring. Listen for the common ones and re-fetch the cart snapshot.
      ['cart:updated', 'cart:refresh', 'cart-drawer:refresh'].forEach(function (
        evt
      ) {
        document.addEventListener(evt, function () {
          refreshCartPricing().then(sendCartPricing);
        });
      });
    }

    // Wait for the iframe to appear; on appearance, hook its load event
    // and also schedule a burst of postMessage sends (covers late rerenders).
    var wired = new WeakSet();

    function wire(frame) {
      if (wired.has(frame)) return;
      wired.add(frame);
      // Refresh the cart snapshot the moment a quote iframe opens, so the
      // quote captures the *current* cart state — not whatever was there
      // when the page first loaded. (No-op on non-cart pages thanks to the
      // gate inside refreshCartPricing.)
      refreshCartPricing().then(sendCartPricing);
      // Send on load
      frame.addEventListener('load', function () {
        sendAll();
        setTimeout(sendAll, 200);
        setTimeout(sendAll, 800);
        setTimeout(sendAll, 1800);
      });
      // Initial attempt (in case the iframe was already loaded by the time
      // we got here)
      sendAll();
    }

    var existing = findQuotifyFrame();
    if (existing) wire(existing);

    var obs = new MutationObserver(function () {
      var f = findQuotifyFrame();
      if (f) wire(f);
    });
    obs.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });

    // Continuous heartbeat — every 1.5s while a Quotify iframe exists,
    // send all messages. Cheap and guarantees we don't miss a re-render
    // (e.g. when the customer changes variants and Quotify rebuilds).
    setInterval(function () {
      if (findQuotifyFrame()) sendAll();
    }, 1500);

    // Also re-send when the URL's variant param changes (variant grid).
    var lastUrl = window.location.href;
    setInterval(function () {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        if (findQuotifyFrame()) sendAtcPrice();
      }
    }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
